import { Check, ChevronDown } from "lucide-react";
import type { FC, ReactNode } from "react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

import { CaseStatus } from "../../../../api/types/case";
import { statusColor } from "../../../../components/case/caseStatusColors";
import { Chip } from "./chip";

export const StatusDot: FC<{ status: CaseStatus; className?: string }> = ({
  status,
  className
}) => (
  <span
    aria-hidden
    className={cn("inline-block size-2 shrink-0 rounded-full", className)}
    style={{ backgroundColor: statusColor(status).color }}
  />
);

/** Dropdown listing every case status; wraps any trigger. */
export const StatusMenu: FC<{
  status: CaseStatus;
  onSelect: (status: CaseStatus) => void;
  align?: "start" | "end";
  children: ReactNode;
}> = ({ status, onSelect, align = "end", children }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
    <DropdownMenuContent align={align} className="w-44">
      {Object.values(CaseStatus).map((s) => (
        <DropdownMenuItem
          key={s}
          className="text-xs"
          onSelect={() => {
            if (s !== status) onSelect(s);
          }}
        >
          <StatusDot status={s} />
          <span className="flex-1">{s}</span>
          {s === status ? <Check className="size-3.5 opacity-70" /> : null}
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

export const StatusChip: FC<{
  status: CaseStatus;
  onSelect: (status: CaseStatus) => void;
  className?: string;
}> = ({ status, onSelect, className }) => (
  <StatusMenu status={status} onSelect={onSelect}>
    <Chip aria-label={`Status: ${status}`} className={className}>
      <StatusDot status={status} />
      <span className="truncate">{status}</span>
      <ChevronDown className="size-3 opacity-60" />
    </Chip>
  </StatusMenu>
);
